import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { PubNubProvider } from "pubnub-react";
import Sidebar from "../../components/Sidebar";
import ChatInterface from "../../components/chat/ChatInterface";
import { getMenteesFromProfile } from "../../services/mentorMenteeChatService";
import { useConversationChat } from "../../hooks/useConversationChat";
import PubNub from "pubnub";

const MentorChatContent = () => {
  const { id: userId, type: userType } = useSelector((state) => state.auth);
  const { data: mentees, isLoading: isLoadingMentees } = useQuery({
    queryKey: ["mentor_mentees", userId],
    queryFn: () => getMenteesFromProfile(userId),
    enabled: !!userId && userType === "mentor",
  });

  const [selectedMentee, setSelectedMentee] = useState(null);

  const { messages, isLoadingConversationMessages, isConnected, sendMessage } =
    useConversationChat(userId, selectedMentee?._id, userId);

  // Set first mentee as selected when mentees are loaded
  useEffect(() => {
    if (mentees && mentees.length > 0 && !selectedMentee) {
      setSelectedMentee(mentees[0]);
    }
  }, [mentees, selectedMentee]);

  const handleSendMessage = async (text) => {
    if (!text.trim() || !selectedMentee) return;
    await sendMessage(text);
  };

  return (
    <Sidebar>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
            <span className="text-white font-bold text-lg">💬</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Chat with Mentees</h1>
            <p className="text-sm text-gray-600">Connect and guide your mentees</p>
          </div>
        </div>
        {isConnected && (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Connected
          </div>
        )}
      </div>

      {isLoadingMentees ? (
        <div className="flex items-center justify-center h-[calc(100vh-200px)] bg-white border border-gray-400 rounded-lg">
          <div className="text-center text-gray-500">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">Loading chats...</h3>
            <p className="text-sm">Fetching mentees and conversations</p>
          </div>
        </div>
      ) : mentees.length === 0 ? (
        <div className="flex items-center justify-center h-[calc(100vh-200px)] bg-white border border-gray-400 rounded-lg">
          <div className="text-center text-gray-500">
            <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
              <svg fill="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">No Mentees Yet</h3>
            <p className="text-sm mb-4">Mentees will appear here once they book appointments with you</p>
            <div className="text-xs text-gray-400">
              <p>• Set your availability in Schedule Timings</p>
              <p>• Update your pricing to be competitive</p>
            </div>
          </div>
        </div>
      ) : (
        <ChatInterface
          items={mentees || []}
          selectedItem={selectedMentee}
          messages={messages}
          isLoadingItems={false}
          isLoadingMessages={isLoadingConversationMessages}
          title="My Mentees"
          leftColumnTitle="My Mentees"
          rightColumnTitle="Chat with Mentee"
          emptyStateTitle="No messages yet"
          emptyStateSubtitle="Send a message to start the conversation"
          sidebarEmptyStateSubtitle="Mentees will appear here once they book appointments"
          inputPlaceholder="Share your thoughts with the mentee..."
          onItemSelect={setSelectedMentee}
          onSendMessage={handleSendMessage}
          selectedItemKey="_id"
        />
      )}
    </Sidebar>
  );
};

const MentorChatPage = () => {
  const userId = useSelector((state) => state.auth.id);
  const pubnub = useMemo(
    () =>
      new PubNub({
        publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY || "demo",
        subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY || "demo",
        uuid: userId || "mentor",
      }),
    [userId]
  );

  return (
    <PubNubProvider client={pubnub}>
      <MentorChatContent />
    </PubNubProvider>
  );
};

export default MentorChatPage;
